import { useState } from "react";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api } from "@/lib/api";
import { useContent } from "@/lib/content";

const OFFICES = [
    "Côte d'Azur",
    "Genève",
    "Londres",
    "Miami",
    "Tokyo",
    "Pékin",
    "Dubaï",
    "Doha",
];

export default function Contact() {
    const content = useContent();
    const cities = content?.cities?.list || OFFICES;
    const contactEmail =
        content?.contact?.email || "contact@xv-capitalpartners.com";
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        subject: "Capital advisory",
        message: "",
        consent: false,
    });
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            setError("Merci de consentir au traitement de vos données.");
            return;
        }
        setStatus("loading");
        setError(null);
        try {
            await api.post(`/contact`, form);
            setStatus("success");
            setForm({
                name: "",
                email: "",
                company: "",
                subject: "Capital advisory",
                message: "",
                consent: false,
            });
        } catch (err) {
            setStatus("error");
            setError(
                err?.response?.data?.detail ||
                    "Une erreur est survenue. Merci de réessayer."
            );
        }
    };

    return (
        <div data-testid="contact-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="pt-32 md:pt-44 pb-16 md:pb-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Premier entretien · Confidentiel
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[48px] sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Initions{" "}
                            <em className="not-italic text-xv-midnight">
                                la conversation
                            </em>
                            .
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-2xl text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.8] font-light">
                            Chaque mission s'ouvre par un entretien
                            confidentiel d'évaluation. Décrivez-nous
                            brièvement votre projet ; nous reprenons
                            contact sous 48 heures ouvrées.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FORM + INFO */}
            <section className="pb-28 md:pb-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-16 lg:gap-24">
                    {/* FORM */}
                    <div className="lg:col-span-7">
                        <Reveal>
                            <form
                                onSubmit={handleSubmit}
                                data-testid="contact-form"
                                className="space-y-10"
                            >
                                <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
                                    <div>
                                        <label className="overline text-xv-gold block mb-3">
                                            Nom complet
                                        </label>
                                        <input
                                            required
                                            data-testid="input-name"
                                            value={form.name}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    name: e.target.value,
                                                })
                                            }
                                            className="xv-input"
                                            placeholder="Mme. / M."
                                        />
                                    </div>
                                    <div>
                                        <label className="overline text-xv-gold block mb-3">
                                            Adresse email
                                        </label>
                                        <input
                                            required
                                            type="email"
                                            data-testid="input-email"
                                            value={form.email}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    email: e.target.value,
                                                })
                                            }
                                            className="xv-input"
                                            placeholder="vous@cabinet.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="overline text-xv-gold block mb-3">
                                            Société / Structure
                                        </label>
                                        <input
                                            data-testid="input-company"
                                            value={form.company}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    company:
                                                        e.target.value,
                                                })
                                            }
                                            className="xv-input"
                                            placeholder="Optionnel"
                                        />
                                    </div>
                                    <div>
                                        <label className="overline text-xv-gold block mb-3">
                                            Nature du projet
                                        </label>
                                        <select
                                            data-testid="input-subject"
                                            value={form.subject}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    subject:
                                                        e.target.value,
                                                })
                                            }
                                            className="xv-input bg-transparent appearance-none cursor-pointer"
                                        >
                                            <option>Capital advisory</option>
                                            <option>
                                                Structuration d'opération
                                            </option>
                                            <option>
                                                Conseil stratégique
                                            </option>
                                            <option>
                                                Advisory dirigeant
                                            </option>
                                            <option>
                                                Réseau & introductions
                                            </option>
                                            <option>Autre</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="overline text-xv-gold block mb-3">
                                        Votre message
                                    </label>
                                    <textarea
                                        required
                                        data-testid="input-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                message: e.target.value,
                                            })
                                        }
                                        className="xv-input"
                                        placeholder="Quelques lignes sur votre projet, votre ambition, votre horizon."
                                    />
                                </div>

                                <label className="flex items-start gap-4 text-sm text-xv-text-secondary font-light leading-relaxed cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="input-consent"
                                        checked={form.consent}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                consent: e.target.checked,
                                            })
                                        }
                                        className="mt-1 accent-xv-midnight w-4 h-4"
                                    />
                                    <span>
                                        Je consens au traitement
                                        confidentiel de ces données pour
                                        la prise de contact. Je peux
                                        révoquer mon consentement à tout
                                        moment.
                                    </span>
                                </label>

                                {error && (
                                    <p
                                        data-testid="contact-error"
                                        className="text-sm text-red-700 font-light"
                                    >
                                        {error}
                                    </p>
                                )}
                                {status === "success" && (
                                    <p
                                        data-testid="contact-success"
                                        className="text-sm text-xv-midnight font-light"
                                    >
                                        Merci. Votre message nous est bien
                                        parvenu — nous reviendrons vers
                                        vous sous 48 heures ouvrées.
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={
                                        status === "loading" ||
                                        !form.consent
                                    }
                                    className="group inline-flex items-center gap-4 px-10 py-5 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-black transition-all duration-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {status === "loading"
                                        ? "Envoi en cours…"
                                        : "Envoyer ma demande"}
                                    <ArrowUpRight
                                        strokeWidth={1.2}
                                        size={16}
                                        className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                                    />
                                </button>
                            </form>
                        </Reveal>
                    </div>

                    {/* INFO */}
                    <div className="lg:col-span-5 lg:pl-8">
                        <Reveal delay={0.1}>
                            <div className="bg-xv-midnightDeep text-xv-ivory p-10 md:p-12">
                                <div className="overline text-xv-goldSoft mb-8">
                                    Coordonnées
                                </div>
                                <ul className="space-y-6 text-[15px] font-sans-modern">
                                    <li className="flex items-start gap-4">
                                        <Mail
                                            strokeWidth={1}
                                            size={18}
                                            className="mt-1 text-xv-goldSoft shrink-0"
                                        />
                                        <a
                                            href={`mailto:${contactEmail}`}
                                            data-testid="contact-email"
                                            className="link-underline"
                                        >
                                            {contactEmail}
                                        </a>
                                    </li>
                                    <li className="flex items-start gap-4">
                                        <MapPin
                                            strokeWidth={1}
                                            size={18}
                                            className="mt-1 text-xv-goldSoft shrink-0"
                                        />
                                        <span className="leading-relaxed">
                                            Sur rendez-vous uniquement
                                        </span>
                                    </li>
                                </ul>

                                <div className="mt-12 pt-8 border-t border-xv-ivory/15 grid grid-cols-2 gap-y-4">
                                    {cities.map((c) => (
                                        <div
                                            key={c}
                                            className="flex items-center gap-3"
                                        >
                                            <span className="h-px w-5 bg-xv-goldSoft" />
                                            <span
                                                className="font-serif text-[20px] md:text-[22px]"
                                                style={{ fontWeight: 400 }}
                                            >
                                                {c}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-12 pt-8 border-t border-xv-ivory/15">
                                    <div className="overline text-xv-goldSoft mb-3">
                                        Disponibilité
                                    </div>
                                    <p className="text-xv-ivory/75 text-[14px] leading-[1.85] font-light">
                                        Nous n'acceptons qu'un nombre
                                        limité de dossiers chaque
                                        trimestre.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
